/**
 * The standard for a failure on our side: report it, say it plainly, and
 * never ask the gardener to try again.
 *
 * The owner's rule (D31, 2026-10-08): when something breaks on Grow's side,
 * it is sent to Sentry, and the gardener is told that the problem has been
 * reported, not to try again, and where to write for help
 * (SUPPORT_EMAIL). Before this, each screen said "Try again" in its own
 * words, and nobody heard about the failure unless the screen happened to
 * log it with console.error.
 *
 * What counts as a failure on our side (isServerFailure): a request that got
 * no answer at all (offline, a dropped connection, the 30-second timeout) or
 * an answer of 500 or more. A refusal (4xx) is not one: it is validation or a
 * limit the gardener can do something about, and the screen keeps its own
 * words for it (lib/errorMessage, the subscription wall, AI_LIMIT notices).
 * A request the app itself cancelled is not one either.
 *
 * The server's request limit (a 429 with the code RATE_LIMITED, B24) is
 * neither: it is a short wait, not a fault, and the server has logged it to
 * Sentry already. isRateLimited names it and rateLimitedText gives its own
 * calm sentence (serverFailure.limited), which names the support address and
 * never says "try again"; lib/errorMessage uses it before any server
 * message, so no screen passes the limit's words through, and
 * serverFailureText answers it too (not reported again), so a screen whose
 * own fallback words say "try again" never reaches them on a 429. The AI allowance's
 * 429 (error AI_LIMIT) is not this: it keeps its own notice.
 *
 * reportServerFailure sends the error to Sentry once, tagged with the
 * endpoint (method and path), the status (or "none" for no answer) and
 * whether the device thought it was online, and returns a short reference
 * code (E3: the first 8 characters of the event id, also the event's
 * "reference" tag, so `reference:d53e8822` finds it in Sentry). The standard
 * sentence ends with "Reference: d53e8822" when there is a code (the
 * server's own `ref` in the error body wins), and has no such line when
 * Sentry is off. serverFailureText does the reporting and hands back the
 * standard sentence, which names the support address; the
 * components in shared/components/ServerFailure draw it with the address as a
 * mailto link.
 *
 * Who uses it: lib/errorMessage (so every screen that words its failures
 * through errorMessage follows the standard), AuthContext (an account that
 * could not be loaded), the contexts and screens that catch a failed request,
 * and shared/components/ServerFailure.
 *
 * Words: serverFailure.message and serverFailure.limited (en, es), with {email};
 * serverFailure.reference, with {code}.
 *
 * @module serverFailure
 */

import axios from 'axios';
import * as Sentry from '@sentry/react';
import { t } from '@/shared/i18n/i18nSetup';
import { SUPPORT_EMAIL } from '@/config/tunables/shared';
import { captureWithReference, joinReference, referenceFromBody, type SentryLike, type CaptureContext } from '@/shared/lib/serverFailureCore';

/** Errors already sent to Sentry, so a failure drawn twice (or retried by React Query) is reported once; each with the reference code its report got (undefined when Sentry is off). */
const reported = new WeakMap<object, string | undefined>();

/**
 * Whether a failure is on Grow's side: no answer at all (offline, dropped,
 * timed out) or an answer of 500 or more. A refusal (4xx) and a request the
 * app cancelled are not.
 *
 * @param error - Whatever was thrown.
 * @returns True for a failure the gardener can do nothing about.
 */
export function isServerFailure(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false;
  if (axios.isCancel(error) || error.code === 'ERR_CANCELED') return false;
  const status = error.response?.status;
  return status === undefined || status >= 500;
}

/**
 * The request a failure was for, as "GET /plants": what Sentry is told, so
 * a report says which endpoint broke without its query string.
 *
 * @param error - An Axios error.
 * @returns The method and path, or "unknown".
 */
function endpointOf(error: unknown): string {
  if (!axios.isAxiosError(error)) return 'unknown';
  const method = (error.config?.method ?? 'get').toUpperCase();
  const path = (error.config?.url ?? '').split('?')[0];
  return path ? `${method} ${path}` : 'unknown';
}

/**
 * Sends a failure to Sentry, once per error, with the endpoint and status as
 * tags. Does nothing without a Sentry DSN (Sentry is then not started).
 *
 * @param error - Whatever was thrown.
 * @param where - Optional name of the screen or step, for the report.
 * @returns The short reference code of the report (also for an error already
 *   reported), or undefined when Sentry is off.
 */
export function reportServerFailure(error: unknown, where?: string): string | undefined {
  if (error && typeof error === 'object' && reported.has(error)) return reported.get(error);
  const status = axios.isAxiosError(error) ? error.response?.status : undefined;
  const statusTag = status === undefined ? 'none' : String(status);
  const endpoint = endpointOf(error);
  const code = captureWithReference(Sentry as unknown as SentryLike, error instanceof Error ? error : new Error(`Server failure: ${endpoint}`), {
    tags: { failure: 'server', endpoint, status: statusTag, ...(where ? { where } : {}) },
    extras: { online: typeof navigator === 'undefined' ? null : navigator.onLine },
    // One issue per endpoint and status, not one per stack trace.
    fingerprint: ['server-failure', endpoint, statusTag],
  });
  if (error && typeof error === 'object') reported.set(error, code);
  return code;
}

/**
 * Sends a failure that is not a request's (a bug, the browser refusing
 * something) to Sentry once, under the gardener-facing reference code, for
 * a screen that then says the standard sentence (serverFailureWords(error)).
 *
 * @param error - Whatever was thrown.
 * @param where - The screen or step, for the report.
 * @param context - More tags, extras or a fingerprint for the event.
 * @param label - The report's message when the error is not an Error.
 * @returns The reference code, or undefined when Sentry is off.
 */
export function reportOtherFailure(error: unknown, where: string, context: CaptureContext = {}, label?: string): string | undefined {
  if (error && typeof error === 'object' && reported.has(error)) return reported.get(error);
  const code = captureWithReference(Sentry as unknown as SentryLike, error instanceof Error ? error : new Error(label ?? `Failure: ${where}`), {
    ...context,
    tags: { where, ...context.tags },
  });
  if (error && typeof error === 'object') reported.set(error, code);
  return code;
}

/**
 * The reference code to show for a failure: the server's own `ref` from the
 * error body (its Sentry event) when it sent one, else the code of the
 * report the app made (reportServerFailure). Undefined when neither exists
 * (Sentry off), so no line is shown.
 *
 * @param error - Whatever was thrown.
 * @returns The 8-character code, or undefined.
 */
export function referenceOf(error: unknown): string | undefined {
  if (axios.isAxiosError(error)) {
    const fromServer = referenceFromBody(error.response?.data);
    if (fromServer) return fromServer;
  }
  return error && typeof error === 'object' ? reported.get(error) : undefined;
}

/**
 * The reference line in the reader's language ("Reference: d53e8822").
 *
 * @param error - A failure that has been reported.
 * @returns The line, or undefined when there is no code.
 */
export function referenceLine(error: unknown): string | undefined {
  const code = referenceOf(error);
  return code ? t('serverFailure.reference', { code }) : undefined;
}

/**
 * The standard sentence for a failure on our side, in the reader's language,
 * naming the support address.
 *
 * @param error - The failure, when it has been reported: its reference line is added.
 * @returns "Something went wrong on our side. We've been told about it. If
 *   you need help, write to us at hello@gardengameplan.com." or its Spanish,
 *   followed by "Reference: d53e8822" when there is a code.
 */
export function serverFailureWords(error?: unknown): string {
  return joinReference(t('serverFailure.message', { email: SUPPORT_EMAIL }), error === undefined ? undefined : referenceLine(error));
}

/**
 * The support address, for a sentence or a link that names it (the problem
 * report sheet's footer line and its sent and failed words). The one place a
 * component outside the failure screens gets it, so none reads the value file.
 *
 * @returns The support e-mail address.
 */
export function supportEmail(): string {
  return SUPPORT_EMAIL;
}

/**
 * Whether a request was refused by the server's request limit (429), as
 * against the AI allowance's 429 (error AI_LIMIT), which has its own notice.
 *
 * @param error - Whatever was thrown.
 * @returns True for the request limit.
 */
export function isRateLimited(error: unknown): boolean {
  if (!axios.isAxiosError(error) || error.response?.status !== 429) return false;
  const body = error.response?.data as { error?: unknown } | undefined;
  return body?.error !== 'AI_LIMIT';
}

/**
 * The sentence for the server's request limit, in the reader's language,
 * naming the support address and never saying "try again".
 *
 * @returns "Grow got a lot of requests from you at once, so it paused
 *   for a moment. ... write to us at hello@gardengameplan.com."
 *   or its Spanish.
 */
export function rateLimitedWords(): string {
  return t('serverFailure.limited', { email: SUPPORT_EMAIL });
}

/**
 * For a request refused by the server's limit: its sentence (rateLimitedWords).
 * Not reported again: the server logged the refusal to Sentry. For anything
 * else: null.
 *
 * @param error - Whatever was thrown.
 * @returns The sentence, or null.
 */
export function rateLimitedText(error: unknown): string | null {
  return isRateLimited(error) ? rateLimitedWords() : null;
}

/**
 * For a failure on our side: reports it and gives the standard sentence.
 * For the server's request limit (a 429, isRateLimited): the limit's own
 * sentence, not reported again (the server logged it), so no caller's own
 * fallback ("try again") is shown for it (B24). For anything else: null, so
 * the caller says what it always said.
 *
 * @param error - Whatever was thrown.
 * @param where - Optional name of the screen or step, for the report.
 * @returns The standard sentence, the limit's sentence, or null.
 */
export function serverFailureText(error: unknown, where?: string): string | null {
  const limited = rateLimitedText(error);
  if (limited) return limited;
  if (!isServerFailure(error)) return null;
  reportServerFailure(error, where);
  return serverFailureWords(error);
}
